// src/whatsappChat/pages/TemplateCreatePage.jsx
import React, { useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faTrash, faUpload } from '@fortawesome/free-solid-svg-icons';
import { useWaNumbers } from '../api/hooks';
import {
  bodyVariableError,
  BUTTON_CAPS,
  TEMPLATE_LIMITS,
  TEMPLATE_MEDIA_CAPS,
  uploadWaTemplateMedia,
  useWaCreateTemplate,
} from '../api/templateHooks';
import { normalizeError } from '../api/client';
import { digitsOnly, fillTemplate, formatBytes, isHttpsUrl, numberLabel, variableCount } from '../api/content';
import { useWaChatContext } from '../components/WaChatLayout';
import { WA_CHAT_PATH } from '../components/WaChatNav';
import { Field } from '../components/Modal';
import { buttonLabel, TemplatePreview } from '../components/TemplatePreview';
import { showToast } from '../../components/toast.jsx';
import '../styles/inbox.css';
import '../styles/compose.css';
import '../styles/templates.css';

const LIST_PATH = `${WA_CHAT_PATH}/templates`;

const HEADER_TYPES = [
  { value: '', label: 'None' },
  { value: 'TEXT', label: 'Text' },
  { value: 'IMAGE', label: 'Image' },
  { value: 'VIDEO', label: 'Video' },
  { value: 'DOCUMENT', label: 'Document' },
  { value: 'LOCATION', label: 'Location' },
];
const MEDIA_HEADERS = ['IMAGE', 'VIDEO', 'DOCUMENT'];
const MEDIA_ACCEPT = { IMAGE: 'image/jpeg,image/png', VIDEO: 'video/mp4', DOCUMENT: 'application/pdf' };

const BUTTON_TYPES = [
  { value: 'QUICK_REPLY', label: 'Quick reply' },
  { value: 'URL', label: 'Link' },
  { value: 'PHONE_NUMBER', label: 'Call' },
  { value: 'COPY_CODE', label: 'Copy code' },
];

const emptyButton = (type) => ({ type, text: '', url: '', phone_number: '', example: '' });

// "Duplicate as new": carry over what can be reused. Media headers and example values cannot.
function initialForm(from, numberId) {
  const buttons = Array.isArray(from?.buttons) ? from.buttons : [];
  return {
    numberId: numberId || '',
    name: from?.name ? `${from.name}_v2` : '',
    language: from?.language || 'en',
    category: ['MARKETING', 'UTILITY'].includes(from?.category) ? from.category : 'UTILITY',
    headerType: from?.header ? 'TEXT' : '',
    header: from?.header || '',
    body: from?.body || '',
    footer: from?.footer || '',
    examples: Array(variableCount(from?.body)).fill(''),
    buttons: buttons
      .filter((b) => BUTTON_TYPES.some((t) => t.value === String(b?.type || '').toUpperCase()))
      .map((b) => ({ ...emptyButton(String(b.type).toUpperCase()), ...b, type: String(b.type).toUpperCase() })),
  };
}

export default function TemplateCreatePage() {
  const { permissions } = useWaChatContext();
  const navigate = useNavigate();
  const location = useLocation();
  const create = useWaCreateTemplate();
  const { data: numbers = [] } = useWaNumbers(Boolean(permissions.inbox.view));

  const [form, setForm] = useState(() => initialForm(location.state?.from, location.state?.numberId));
  const [media, setMedia] = useState(null); // { file, rowId }
  const [uploading, setUploading] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const fileRef = useRef(null);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const busy = uploading || create.isPending;
  const isMediaHeader = MEDIA_HEADERS.includes(form.headerType);
  const varCount = bodyVariableError(form.body) ? 0 : variableCount(form.body);

  const setBody = (body) => {
    const count = bodyVariableError(body) ? 0 : variableCount(body);
    setForm((prev) => ({
      ...prev,
      body,
      examples: Array.from({ length: count }, (_, i) => prev.examples[i] || ''),
    }));
  };

  const setButton = (index, patch) =>
    set({ buttons: form.buttons.map((b, i) => (i === index ? { ...b, ...patch } : b)) });

  const countOf = (type) => form.buttons.filter((b) => b.type === type).length;
  const canAdd = (type) =>
    form.buttons.length < TEMPLATE_LIMITS.buttons && (!BUTTON_CAPS[type] || countOf(type) < BUTTON_CAPS[type]);

  // ---- Validation (mirrors the backend so Meta does not reject it hours later) ----
  const errors = {};
  if (!form.name) errors.name = 'Required';
  else if (!/^[a-z0-9_]+$/.test(form.name)) errors.name = 'Lowercase letters, digits and underscores only';
  if (!form.language.trim()) errors.language = 'Required';

  if (form.headerType === 'TEXT') {
    if (!form.header.trim()) errors.header = 'Required';
    else if (form.header.length > TEMPLATE_LIMITS.header) errors.header = `At most ${TEMPLATE_LIMITS.header} characters`;
  }
  if (isMediaHeader && !media) errors.media = 'Choose a file';

  if (!form.body.trim()) errors.body = 'Required';
  else if (form.body.length > TEMPLATE_LIMITS.body) errors.body = `At most ${TEMPLATE_LIMITS.body} characters`;
  else if (bodyVariableError(form.body)) errors.body = bodyVariableError(form.body);

  form.examples.forEach((value, i) => {
    if (!value.trim()) errors[`example-${i}`] = 'Required';
  });
  if (form.footer.length > TEMPLATE_LIMITS.footer) errors.footer = `At most ${TEMPLATE_LIMITS.footer} characters`;

  form.buttons.forEach((b, i) => {
    const key = `button-${i}`;
    if (b.type === 'COPY_CODE') {
      if (!b.example.trim()) errors[key] = 'Enter a sample code';
      else if (b.example.trim().length > TEMPLATE_LIMITS.copyCode) errors[key] = `Sample code: at most ${TEMPLATE_LIMITS.copyCode} characters`;
      return;
    }
    if (!b.text.trim()) errors[key] = 'Label is required';
    else if (b.text.length > TEMPLATE_LIMITS.buttonText) errors[key] = `Label: at most ${TEMPLATE_LIMITS.buttonText} characters`;
    else if (b.type === 'URL' && !isHttpsUrl(b.url)) errors[key] = 'Enter an https link';
    else if (b.type === 'PHONE_NUMBER' && digitsOnly(b.phone_number).length < 10) errors[key] = 'Enter the phone number with country code';
  });

  const err = (key) => (showErrors ? errors[key] : null);

  const onPickMedia = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const cap = TEMPLATE_MEDIA_CAPS[form.headerType.toLowerCase()];
    if (file.size > cap) {
      showToast.error(`The file is ${formatBytes(file.size)}. The limit for this header is ${formatBytes(cap)}.`);
      return;
    }
    setMedia({ file, rowId: null });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (Object.keys(errors).length) {
      setShowErrors(true);
      showToast.error('Fix the highlighted fields first.');
      return;
    }

    let rowId = media?.rowId || null;
    if (isMediaHeader && !rowId) {
      setUploading(true);
      try {
        rowId = await uploadWaTemplateMedia(media.file, form.headerType.toLowerCase(), form.numberId);
        // Kept so a failed submit does not upload the file again.
        setMedia((prev) => (prev ? { ...prev, rowId } : prev));
      } catch (error) {
        showToast.error(normalizeError(error).message);
        return;
      } finally {
        setUploading(false);
      }
    }

    const payload = {
      ...(form.numberId ? { number_id: form.numberId } : {}),
      name: form.name,
      language: form.language.trim(),
      category: form.category,
      body: form.body.trim(),
      ...(form.headerType ? { header_type: form.headerType } : {}),
      ...(form.headerType === 'TEXT' ? { header: form.header.trim() } : {}),
      ...(isMediaHeader ? { components: { header_media_row_id: rowId } } : {}),
      ...(form.footer.trim() ? { footer: form.footer.trim() } : {}),
      ...(form.examples.length ? { example_values: form.examples.map((v) => v.trim()) } : {}),
      ...(form.buttons.length
        ? {
            buttons: form.buttons.map((b) => {
              if (b.type === 'COPY_CODE') return { type: b.type, example: b.example.trim() };
              if (b.type === 'URL') return { type: b.type, text: b.text.trim(), url: b.url.trim() };
              if (b.type === 'PHONE_NUMBER') {
                return { type: b.type, text: b.text.trim(), phone_number: b.phone_number.trim() };
              }
              return { type: b.type, text: b.text.trim() };
            }),
          }
        : {}),
    };

    try {
      await create.mutateAsync(payload);
      showToast.success('Template submitted. It stays Pending until Meta reviews it.');
      navigate(LIST_PATH);
    } catch (error) {
      showToast.error(normalizeError(error).message);
    }
  };

  return (
    <section className="wa-page wa-page-wide">
      <div className="wa-page-head">
        <div>
          <Link className="wa-back-link" to={LIST_PATH}>
            <FontAwesomeIcon icon={faArrowLeft} /> Templates
          </Link>
          <h1 className="wa-page-title">Create template</h1>
          <p className="wa-page-sub">
            The template is submitted to Meta as soon as you create it and cannot be edited afterwards.
          </p>
        </div>
      </div>

      <form className="wa-create" onSubmit={submit} noValidate>
        <div className="wa-create-form">
          <div className="wa-card wa-card-pad wa-form">
            <h2 className="wa-card-title">Basics</h2>
            {numbers.length > 1 && (
              <Field label="Business number">
                <select className="wa-input" value={form.numberId} onChange={(e) => set({ numberId: e.target.value })}>
                  <option value="">Default number</option>
                  {numbers.map((n) => (
                    <option key={n.number_id} value={n.number_id}>{numberLabel(n)}</option>
                  ))}
                </select>
              </Field>
            )}
            <Field label="Name" hint="Lowercase letters, digits and underscores. Example: order_shipped" error={err('name')}>
              <input
                className="wa-input"
                type="text"
                value={form.name}
                onChange={(e) => set({ name: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
              />
            </Field>
            <div className="wa-field-pair">
              <Field label="Category">
                <select className="wa-input" value={form.category} onChange={(e) => set({ category: e.target.value })}>
                  <option value="UTILITY">Utility</option>
                  <option value="MARKETING">Marketing</option>
                </select>
              </Field>
              <Field label="Language code" hint="For example en, en_US, hi" error={err('language')}>
                <input className="wa-input" type="text" value={form.language} onChange={(e) => set({ language: e.target.value })} />
              </Field>
            </div>
          </div>

          <div className="wa-card wa-card-pad wa-form">
            <h2 className="wa-card-title">Content</h2>
            <Field label="Header">
              <select
                className="wa-input"
                value={form.headerType}
                onChange={(e) => {
                  set({ headerType: e.target.value });
                  setMedia(null);
                }}
              >
                {HEADER_TYPES.map((h) => (
                  <option key={h.value} value={h.value}>{h.label}</option>
                ))}
              </select>
            </Field>

            {form.headerType === 'TEXT' && (
              <Field label="Header text" count={form.header.length} max={TEMPLATE_LIMITS.header} error={err('header')}>
                <input className="wa-input" type="text" value={form.header} onChange={(e) => set({ header: e.target.value })} />
              </Field>
            )}

            {isMediaHeader && (
              <div className="wa-field">
                <span className="wa-field-label">Sample {form.headerType.toLowerCase()}</span>
                <input ref={fileRef} type="file" hidden accept={MEDIA_ACCEPT[form.headerType]} onChange={onPickMedia} />
                <span className="wa-input-row">
                  <button type="button" className="wa-btn wa-btn-secondary" onClick={() => fileRef.current?.click()} disabled={busy}>
                    <FontAwesomeIcon icon={faUpload} /> {media ? 'Change file' : 'Choose file'}
                  </button>
                  {media && (
                    <span className="wa-muted wa-ellipsis">
                      {media.file.name} · {formatBytes(media.file.size)}
                    </span>
                  )}
                </span>
                {err('media') ? (
                  <span className="wa-field-error">{err('media')}</span>
                ) : (
                  <span className="wa-field-hint">
                    Up to {formatBytes(TEMPLATE_MEDIA_CAPS[form.headerType.toLowerCase()])}. Uploaded when you submit.
                  </span>
                )}
              </div>
            )}

            <Field
              label="Body"
              count={form.body.length}
              max={TEMPLATE_LIMITS.body}
              hint="Use {{1}}, {{2}} … for the parts that change per customer."
              error={err('body')}
            >
              <textarea className="wa-input" rows={5} value={form.body} onChange={(e) => setBody(e.target.value)} />
            </Field>

            {varCount > 0 && (
              <div className="wa-field-pair">
                {form.examples.map((value, i) => (
                  <Field key={i} label={`Sample for {{${i + 1}}}`} error={err(`example-${i}`)}>
                    <input
                      className="wa-input"
                      type="text"
                      value={value}
                      onChange={(e) =>
                        set({ examples: form.examples.map((v, idx) => (idx === i ? e.target.value : v)) })
                      }
                    />
                  </Field>
                ))}
              </div>
            )}

            <Field label="Footer (optional)" count={form.footer.length} max={TEMPLATE_LIMITS.footer} error={err('footer')}>
              <input className="wa-input" type="text" value={form.footer} onChange={(e) => set({ footer: e.target.value })} />
            </Field>
          </div>

          <div className="wa-card wa-card-pad wa-form">
            <h2 className="wa-card-title">Buttons (optional)</h2>
            {form.buttons.map((b, i) => (
              <div key={i} className="wa-field">
                <span className="wa-field-label">
                  {BUTTON_TYPES.find((t) => t.value === b.type)?.label}
                </span>
                <span className="wa-input-row">
                  {b.type === 'COPY_CODE' ? (
                    <input
                      className="wa-input"
                      type="text"
                      placeholder="Sample code, e.g. SAVE20"
                      value={b.example}
                      onChange={(e) => setButton(i, { example: e.target.value })}
                    />
                  ) : (
                    <input
                      className="wa-input"
                      type="text"
                      placeholder="Label"
                      maxLength={TEMPLATE_LIMITS.buttonText}
                      value={b.text}
                      onChange={(e) => setButton(i, { text: e.target.value })}
                    />
                  )}
                  {b.type === 'URL' && (
                    <input
                      className="wa-input"
                      type="url"
                      placeholder="https://example.com/track/{{1}}"
                      value={b.url}
                      onChange={(e) => setButton(i, { url: e.target.value })}
                    />
                  )}
                  {b.type === 'PHONE_NUMBER' && (
                    <input
                      className="wa-input"
                      type="tel"
                      placeholder="+919876543210"
                      value={b.phone_number}
                      onChange={(e) => setButton(i, { phone_number: e.target.value })}
                    />
                  )}
                  <button
                    type="button"
                    className="wa-icon-btn"
                    aria-label={`Remove button ${i + 1}`}
                    onClick={() => set({ buttons: form.buttons.filter((_, idx) => idx !== i) })}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </span>
                {err(`button-${i}`) && <span className="wa-field-error">{err(`button-${i}`)}</span>}
              </div>
            ))}

            <div className="wa-add-buttons">
              {BUTTON_TYPES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  className="wa-btn wa-btn-secondary"
                  disabled={!canAdd(t.value)}
                  onClick={() => set({ buttons: [...form.buttons, emptyButton(t.value)] })}
                >
                  + {t.label}
                </button>
              ))}
            </div>
            <span className="wa-field-hint">
              Up to {TEMPLATE_LIMITS.buttons} buttons: at most 2 links, 1 call and 1 copy code.
            </span>
          </div>

          <div className="wa-modal-actions">
            <Link className="wa-btn wa-btn-secondary" to={LIST_PATH}>Cancel</Link>
            <button type="submit" className="wa-btn wa-btn-primary" disabled={busy}>
              {uploading ? 'Uploading…' : create.isPending ? 'Submitting…' : 'Create and submit'}
            </button>
          </div>
        </div>

        <aside className="wa-create-preview">
          <span className="wa-field-label">Preview</span>
          <TemplatePreview
            headerType={form.headerType}
            header={form.header}
            body={fillTemplate(form.body, form.examples)}
            footer={form.footer}
            buttons={form.buttons.map(buttonLabel)}
          />
        </aside>
      </form>
    </section>
  );
}
